import { Handshake, Home } from "@mui/icons-material";
import moment from "moment";
import React, { useCallback, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import ReusableTable from "../../../../components/custom/data_table/ReusableTable";
import IconBreadcrumbs from "../../../../components/custom/IconBreadcrumbs";
import {
  useGetAgreementReportsQuery,
  useGetCopyrightReportsQuery,
  useLazyGetAgreementReportsPrintQuery,
} from "../../../../redux/api/reportApiSlice";
import { localDateFormat } from "../../../../utils/dateFormat";
import CommonAgreementCopyright from "./CommonAgreementCopyright";
import PreviewAndPrint from "./PreviewAndPrint";

const AgreementReport = () => {
  const currentDate = moment().format("YYYY-MM-DD"); // Current date
  const threeMonthsAgo = moment().subtract(2, "months").format("YYYY-MM-DD");
  const [fromDate, setFromDate] = useState(threeMonthsAgo);
  const [toDate, setToDate] = useState(currentDate);
  const [category_id, setCategoryId] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [page, setPage] = useState(1);

  const [contentReady, setContentReady] = useState(false);
  const printRef = useRef(null);

  const { data, isLoading, refetch } = useGetAgreementReportsQuery({
    fromDate,
    toDate,
    category_id,
  });

  const [getPrintData, { data: dataPrint, isLoading: isLoadingPrint }] =
    useLazyGetAgreementReportsPrintQuery();

  /// copyright application length
  const { data: copyright } = useGetCopyrightReportsQuery();
  /// agreement application length
  const { data: agreement } = useGetAgreementReportsQuery();

  const total = {
    copyright: copyright?.meta?.total ?? 0,
    agreement: agreement?.meta?.total ?? 0,
  };

  const breadcrumbsData = [
    { icon: Home, title: "হোম", url: "/" },
    {
      icon: Handshake,
      title: "চুক্তিপত্র - প্রতিবেদন",
      active: true,
    },
  ];

  // data table start
  const totalData = data?.meta?.total;
  const lastPage = data?.meta?.last_page;

  const handlePrint = useReactToPrint({
    documentTitle: "Print application",
    content: () => printRef.current,
  });

  const initiatePrint = () => {
    setContentReady(false); // Reset the content ready state

    getPrintData({
      fromDate,
      toDate,
    }).then(() => {
      // Trigger print only when the content is ready
      if (contentReady) {
        handlePrint();
      }
    });
  };

  const handleContentReady = useCallback(() => {
    setContentReady(true);
    if (printRef.current) {
      handlePrint(); // Trigger print once content is ready
    }
  }, []);

  const handlePageChange = (page) => {
    setPage(page);
    refetch();
  };

  const columns = [
    { label: "তারিখ", name: "sendDate" },
    {
      label: "আবেদন নং",
      name: "trackReg",
    },
    { label: "আবেদনকারির নাম", name: "applicant" },
    {
      label: "শিরোনাম",
      name: "workTitle",
    },

    { label: "বিভাগ", name: "category" },
    { label: "উপবিভাগ", name: "subCategory" },
  ];

  const dataTable = data?.data.map((item, i) => ({
    sendDate: localDateFormat(item?.sendDate),
    trackReg: item?.trackReg,
    id: item?.id,
    applicant: item?.puname,
    workTitle: item?.document?.workTitle,
    category: item?.document?.category?.name,
    subCategory: item?.document?.subCategory?.name,
  }));

  const applicationPath = {
    agreement: "/admin/agreement-report",
    copyright: "/admin/copyright-report",
  };

  const pnt_data = dataPrint?.data.map((item) => ({
    sendDate: localDateFormat(item?.sendDate),
    trackReg: item?.trackReg,
    id: item?.id,
    applicant: item?.puname,
    workTitle: item?.document?.workTitle,
    category: item?.document?.category?.name,
    subCategory: item?.document?.subCategory?.name,
  }));

  return (
    <div>
      <div className="py-3">
        <IconBreadcrumbs breadcrumbs={breadcrumbsData} />
      </div>
      <div className="slideUp dashboard-layout p-4">
        <div className="pb-1">
          <CommonAgreementCopyright
            total={total}
            handlePrint={initiatePrint}
            applicationPath={applicationPath}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            category_id={category_id}
            setCategoryId={setCategoryId}
            subCategory={subCategory}
            setSubCategory={setSubCategory}
          />
        </div>
        <ReusableTable
          columns={columns}
          data={dataTable}
          isLoading={isLoading}
          currentPage={page}
          lastPage={lastPage}
          totalData={totalData}
          onPageChange={handlePageChange}
        />
      </div>
      <div>
        <PreviewAndPrint
          data={pnt_data}
          isLoading={isLoadingPrint}
          onContentReady={handleContentReady}
          date={{
            fromDate,
            toDate,
            title: "চুক্তিপত্র নিবন্ধন",
          }}
          printRef={printRef}
        />
      </div>
    </div>
  );
};

export default AgreementReport;
